import React, { ReactNode } from 'react';
import { Table } from '../../components/Table';
import { Modal } from '../../components/Modal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useCRUDModal } from '../../hooks/useCRUDModal';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';

export interface Column {
  key: string;
  title: string;
  width: string;
}

export interface ReferencePageProps<T extends { id: string }> {
  title: string;
  items: T[];
  columns: readonly Column[] | Column[];
  onAdd: (data: Omit<T, 'id' | 'created_at' | 'is_deleted'>) => void;
  onEdit: (id: string, data: Omit<T, 'id' | 'created_at' | 'is_deleted'>) => void;
  onDelete: (id: string) => void;
  renderForm: (props: {
    mode: 'add' | 'edit';
    theme: any;
    initialData?: T;
    onSubmit: (data: Omit<T, 'id' | 'created_at' | 'is_deleted'>) => void;
    onCancel: () => void;
  }) => ReactNode;
}

/**
 * Универсальная страница для справочников
 * Предоставляет стандартный UI для CRUD операций
 */
export function ReferencePage<T extends { id: string }>({
  title,
  items,
  columns,
  onAdd,
  onEdit,
  onDelete,
  renderForm,
}: ReferencePageProps<T>) {
  const { t } = useLanguage();
  const { theme } = useTheme();
  const modal = useCRUDModal<T>();

  const handleAdd = (data: Omit<T, 'id' | 'created_at' | 'is_deleted'>) => {
    onAdd(data);
    modal.closeModal();
  };

  const handleEdit = (data: Omit<T, 'id' | 'created_at' | 'is_deleted'>) => {
    if (modal.editingItem) {
      onEdit(modal.editingItem.id, data);
      modal.closeModal();
    }
  };

  const handleDelete = () => {
    if (modal.deleteItemId) {
      onDelete(modal.deleteItemId);
      modal.closeDeleteModal();
    }
  };

  return (
    <div className={`content-section ${theme.isDark ? 'dark' : 'light'}`}>
      <div className="section-header">
        <h2>{title}</h2>
        <button
          className={`add-button ${theme.isDark ? 'dark' : 'light'}`}
          onClick={modal.openAddModal}
        >
          + {t('common.add')}
        </button>
      </div>

      <Table
        theme={theme}
        title={title}
        data={items}
        columns={columns as any}
        onEdit={modal.openEditModal}
        onDelete={modal.openDeleteModal}
      />

      <Modal
        theme={theme}
        isOpen={modal.isOpen}
        onClose={modal.closeModal}
        title={modal.mode === 'add' ? t('common.add') : t('common.edit')}
      >
        {renderForm({
          mode: modal.mode || 'add',
          theme,
          initialData: modal.editingItem || undefined,
          onSubmit: modal.mode === 'add' ? handleAdd : handleEdit,
          onCancel: modal.closeModal,
        })}
      </Modal>

      <ConfirmModal
        theme={theme}
        title={t('common.confirmDelete')}
        isOpen={modal.isDeleteModalOpen}
        onConfirm={handleDelete}
        onCancel={modal.closeDeleteModal}
        message={t('common.confirmDelete')}
      />
    </div>
  );
}
